import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Sparkles,
    Users,
    HeartHandshake,
    TrendingUp,
    Clock,
    ShieldCheck,
    AlertCircle,
    Info,
} from 'lucide-react';
import MetricInfoPopover from '../common/MetricInfoPopover';
import styles from './CreatorPerformanceCard.module.scss';

const hasValue = (val) => val !== null && val !== undefined && val !== '';

const formatCurrency = (amount) => {
    if (!hasValue(amount)) return '—';
    const num = Number(amount);
    if (isNaN(num)) return '—';
    return `$${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatInteger = (val) => {
    if (!hasValue(val)) return '—';
    const num = Number(val);
    if (isNaN(num)) return '—';
    return num.toLocaleString();
};

const formatRate = (rate) => {
    if (!hasValue(rate)) return '—';
    const num = Number(rate);
    if (isNaN(num)) return '—';
    return `${num.toFixed(2)}%`;
};

const CreatorPerformanceCard = ({
    performance,
    loading = false,
    error = null,
    onRefresh = null,
}) => {
    const { t } = useTranslation('creator');

    if (loading) {
        return (
            <div className={styles['performance-card']} aria-busy="true" aria-label={t('performance.loading', 'Loading performance metrics...')}>
                <div className={styles['card-header']}>
                    <div className={styles['header-copy']}>
                        <div className={`${styles['skeleton-pulse']}`} style={{ width: 140, height: 16, marginBottom: 8 }} />
                        <div className={`${styles['skeleton-pulse']}`} style={{ width: 220, height: 26 }} />
                    </div>
                </div>
                <div className={styles['emv-hero-block']}>
                    <div className={`${styles['skeleton-pulse']}`} style={{ width: 180, height: 16, marginBottom: 12 }} />
                    <div className={`${styles['skeleton-pulse']}`} style={{ width: 160, height: 44, marginBottom: 8 }} />
                    <div className={`${styles['skeleton-pulse']}`} style={{ width: '80%', height: 14 }} />
                </div>
                <div className={styles['metrics-grid']}>
                    {[1, 2, 3].map((i) => (
                        <div key={i} className={styles['metric-card']}>
                            <div className={`${styles['skeleton-pulse']}`} style={{ width: 80, height: 14, marginBottom: 8 }} />
                            <div className={`${styles['skeleton-pulse']}`} style={{ width: 100, height: 28 }} />
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles['performance-card']}>
                <div className={styles['status-notice']}>
                    <AlertCircle size={18} />
                    <div className={styles['notice-copy']}>
                        <strong>{t('common.error', 'Error')}</strong>
                        <p>{error}</p>
                    </div>
                </div>
            </div>
        );
    }

    const emv = performance?.estimated_media_value;
    const reach = performance?.reach;
    const engagements = performance?.engagements;
    const engagementRate = performance?.engagement_rate;
    const contentCount = performance?.content_count ?? 0;
    const status = performance?.status || (contentCount === 0 ? 'no_content' : (hasValue(emv) ? 'available' : 'pending'));

    const isAvailable = status === 'available' && hasValue(emv);
    const isPending = status === 'pending';
    const isNoContent = status === 'no_content' || contentCount === 0;

    return (
        <section className={styles['performance-card']} aria-label={t('performance.title', 'Your Performance')}>
            {/* Header */}
            <header className={styles['card-header']}>
                <div className={styles['header-copy']}>
                    <div className={styles['eyebrow']}>
                        <Sparkles size={13} aria-hidden="true" />
                        <span>{t('performance.title', 'Your Performance')}</span>
                    </div>
                    <h3>{t('performance.title', 'Your Performance')}</h3>
                    <p>{t('performance.subtitle', 'Performance metrics generated by your approved content in this collaboration.')}</p>
                </div>
                <div>
                    {isAvailable && (
                        <span className={`${styles['status-badge']} ${styles['status-available']}`}>
                            <ShieldCheck size={13} aria-hidden="true" />
                            {t('performance.status_available', 'Verified')}
                        </span>
                    )}
                    {isPending && (
                        <span className={`${styles['status-badge']} ${styles['status-pending']}`}>
                            <Clock size={13} aria-hidden="true" />
                            {t('performance.status_pending', 'Syncing metrics')}
                        </span>
                    )}
                    {isNoContent && (
                        <span className={`${styles['status-badge']} ${styles['status-empty']}`}>
                            {t('performance.status_no_content', 'No content submitted yet')}
                        </span>
                    )}
                </div>
            </header>

            {/* 1. HERO METRIC: Estimated Media Value (EMV) */}
            <div className={styles['emv-hero-block']}>
                <div className={styles['emv-top-row']}>
                    <div className={styles['emv-label-wrap']}>
                        <span className={styles['emv-label']}>{t('performance.emv_label', 'Estimated Media Value')}</span>
                        <MetricInfoPopover
                            title={t('performance.emv_tooltip_title', 'Estimated Media Value (EMV)')}
                            description={t('performance.emv_tooltip_desc', 'EMV estimates the media value generated by your content based on its performance. It is not a payment or guaranteed monetary value.')}
                            formula={t('performance.emv_tooltip_formula', 'Base Media Value (Impressions + Reach + Video Views CPM) + Engagement Value (Likes + Comments + Shares + Saves)')}
                            formulaLabel={t('collab.report.tables.formula', 'Calculation')}
                            source="Instagram Graph API & Rate Card"
                            hoverable
                            label={t('performance.emv_tooltip_title', 'Estimated Media Value (EMV)')}
                        />
                    </div>
                </div>

                <div className={`${styles['emv-amount']} ${!isAvailable ? styles['is-unavailable'] : ''}`}>
                    {formatCurrency(emv)}
                </div>

                <p className={styles['emv-subtitle']}>
                    {t('performance.emv_subtitle', 'Estimated value of the media exposure generated by your approved content.')}
                </p>
            </div>

            {/* 2. SECONDARY METRICS GRID: Reach, Engagements, Engagement Rate */}
            <div className={styles['metrics-grid']}>
                {/* Reach */}
                <article className={styles['metric-card']}>
                    <div className={styles['metric-header']}>
                        <Users size={15} aria-hidden="true" />
                        <span>{t('performance.reach_label', 'Reach')}</span>
                    </div>
                    <strong className={`${styles['metric-value']} ${!hasValue(reach) ? styles['is-unavailable'] : ''}`}>
                        {formatInteger(reach)}
                    </strong>
                    <span className={styles['metric-helper']}>{t('performance.reach_desc', 'Verified accounts reached')}</span>
                </article>

                {/* Engagements */}
                <article className={styles['metric-card']}>
                    <div className={styles['metric-header']}>
                        <HeartHandshake size={15} aria-hidden="true" />
                        <span>{t('performance.engagements_label', 'Engagements')}</span>
                    </div>
                    <strong className={`${styles['metric-value']} ${!hasValue(engagements) ? styles['is-unavailable'] : ''}`}>
                        {formatInteger(engagements)}
                    </strong>
                    <span className={styles['metric-helper']}>{t('performance.engagements_desc', 'Likes, comments, shares & saves')}</span>
                </article>

                {/* Engagement Rate */}
                <article className={styles['metric-card']}>
                    <div className={styles['metric-header']}>
                        <TrendingUp size={15} aria-hidden="true" />
                        <span>{t('performance.er_label', 'Engagement Rate')}</span>
                    </div>
                    <strong className={`${styles['metric-value']} ${!hasValue(engagementRate) ? styles['is-unavailable'] : ''}`}>
                        {formatRate(engagementRate)}
                    </strong>
                    <span className={styles['metric-helper']}>{t('performance.er_desc', 'Interactions / Reach')}</span>
                </article>
            </div>

            {/* State notices */}
            {isNoContent && (
                <div className={styles['status-notice']}>
                    <Info size={16} aria-hidden="true" />
                    <div className={styles['notice-copy']}>
                        <strong>{t('performance.empty_title', 'Performance data not available yet')}</strong>
                        <p>{t('performance.empty_desc', 'Deliver your approved content to measure your reach, engagements, and Estimated Media Value.')}</p>
                    </div>
                </div>
            )}

            {!isNoContent && isPending && (
                <div className={styles['status-notice']}>
                    <Clock size={16} aria-hidden="true" />
                    <div className={styles['notice-copy']}>
                        <strong>{t('performance.pending_title', 'Performance data not available yet')}</strong>
                        <p>{t('performance.pending_desc', 'FOTH is synchronizing your content metrics from Instagram. Verified data will display automatically.')}</p>
                    </div>
                </div>
            )}

            {/* Micro disclaimer separating EMV from payment/earnings */}
            <footer className={styles['disclaimer-footer']}>
                <Info size={12} aria-hidden="true" />
                <span>{t('performance.disclaimer', 'EMV is an analytical valuation of media exposure, separate from collaboration compensation or payout.')}</span>
            </footer>
        </section>
    );
};

export default CreatorPerformanceCard;
